import React, { useState } from "react";
import styled from "styled-components";
import { Info, ChevronRight } from "lucide-react";
import { useSecureChat } from "./SecureChatContext";
import GroupInfoModal from "./GroupInfoModal";
import ProfileImage from "../../Componets/ProfileImage";
const Constant = require("../Constant");

const MessageHeader = ({ chat, onClose }) => {
  const { currentUser, typingUsers } = useSecureChat();
  const [showGroupInfo, setShowGroupInfo] = useState(false);

  // Get other users in the chat (exclude current user)
  const otherUsers = chat?.otherUsers || [];
  const isGroupChat = chat?.isGroupChat || false;

  // Get display info
  const displayName = chat?.displayName || "Unknown Chat";
  const displayPic = chat?.displayPic || "";

  // Proxy URL function for profile pictures
  const proxiedUrl = (url) => {
    if (!url || url.includes("story-tent-testing.s3.ap-south-1.amazonaws.com")) return url;
    return `${Constant.apiBasePath}proxy-image?url=${encodeURIComponent(url)}`;
  };

  // Get typing indicator text
  const getTypingText = () => {
    if (!chat?._id) return "";

    const chatTypingUsers = typingUsers[chat._id] || {};
    const typingUserIds = Object.keys(chatTypingUsers).filter(
      (userId) => chatTypingUsers[userId] && userId !== currentUser._id
    );

    if (typingUserIds.length === 0) return "";

    if (typingUserIds.length === 1) {
      const typingUser = otherUsers.find(
        (user) => user._id === typingUserIds[0]
      );
      return `${typingUser?.name || "Someone"} is typing...`;
    } else if (typingUserIds.length === 2) {
      const typingUser1 = otherUsers.find(
        (user) => user._id === typingUserIds[0]
      );
      const typingUser2 = otherUsers.find(
        (user) => user._id === typingUserIds[1]
      );
      return `${typingUser1?.name || "Someone"} and ${
        typingUser2?.name || "someone"
      } are typing...`;
    } else {
      return "Several people are typing...";
    }
  };

  const typingText = getTypingText();

  return (
    <Container>
      <LeftSection>
        <AvatarContainer>
          <ProfileImage
            src={displayPic}
            name={displayName}
            size={40}
            alt={displayName}
            proxiedUrl={proxiedUrl}
            avatarProps={{
              border: "2px solid var(--color-surface)",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
            imgStyle={{
              border: "2px solid var(--color-surface)",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          />
          {isGroupChat && <GroupIndicator>G</GroupIndicator>}
        </AvatarContainer>

        <InfoSection>
          <ChatName>{displayName}</ChatName>
          {typingText ? (
            <TypingText>{typingText}</TypingText>
          ) : isGroupChat ? (
            <ClickableStatusText onClick={() => setShowGroupInfo(true)}>
              <span>{`${otherUsers.length + 1} members`}</span>
              <ChevronRight size={14} />
            </ClickableStatusText>
          ) : null}
        </InfoSection>
      </LeftSection>

      <RightSection>
        {isGroupChat && (
          <InfoButton onClick={() => setShowGroupInfo(true)} title="Group Info">
            <Info size={16} />
          </InfoButton>
        )}
        <CloseButton onClick={onClose} title="Close chat">
          <CloseIcon>×</CloseIcon>
        </CloseButton>
      </RightSection>

      {showGroupInfo && (
        <GroupInfoModal chat={chat} onClose={() => setShowGroupInfo(false)} />
      )}
    </Container>
  );
};

export default MessageHeader;

// Styled Components
const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background-color: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  min-height: 60px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
`;

const AvatarContainer = styled.div`
  position: relative;
  margin-right: 12px;
  flex-shrink: 0;
`;

// Avatar and DefaultAvatar are now handled by ProfileImage component

const GroupIndicator = styled.div`
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 14px;
  height: 14px;
  background-color: var(--color-primary, #28a745);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  font-weight: 600;
  border: 2px solid var(--color-surface);
`;

const InfoSection = styled.div`
  flex: 1;
  min-width: 0;
`;

const ChatName = styled.h3`
  margin: 0 0 2px 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--color-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: "Merriweather", serif;
`;

const StatusText = styled.p`
  margin: 0;
  font-size: 12px;
  color: var(--color-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ClickableStatusText = styled.div`
  margin: 0;
  font-size: 12px;
  color: var(--color-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: var(--color-text);
    opacity: 0.9;
  }

  svg {
    flex-shrink: 0;
  }
`;

const TypingText = styled.p`
  margin: 0;
  font-size: 12px;
  color: var(--color-text-secondary);
  font-style: italic;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.6;
    }
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--color-text);
  transition: all 0.2s ease;

  &:hover {
    background-color: var(--color-background);
    color: var(--color-primary);
  }

  &:active {
    background-color: var(--color-border);
  }

  @media (min-width: 769px) {
    display: none;
  }
`;

const CloseIcon = styled.span`
  font-size: 20px;
  font-weight: 600;
`;

const InfoButton = styled.button`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--color-text);
  transition: all 0.2s ease;
  margin-right: 8px;
  padding: 4px;

  &:hover {
    opacity: 0.7;
    background-color: var(--color-background);
    border-radius: 50%;
  }
`;
